import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { CampaignType, EntityIdType, SbCreative } from "@front/m19-api-client";
import { BrandAssetsService } from "@front/m19-services";
import { ActivityComponent, ActivityFilter } from "@m19-board/activities/activity/activity.component";
import { StrategyEx } from "@front/m19-models";
import { map, Observable, of, switchMap } from "rxjs";

@Component({
  selector: "app-strategy-activity-component",
  template: ` <div class="activity-section">
    @if (activityFilter(); as activityFilter) {
      <app-activity-component
        [activityFilter]="activityFilter"
        [hideEntityColumn]="true"
        gridConfigStorageKey="strategyActivities"
      ></app-activity-component>
    }
  </div>`,
  imports: [ActivityComponent],
  styles: [".activity-section {height: 30rem;}"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyActivityComponent {
  private readonly brandAssetsService = inject(BrandAssetsService);

  readonly strategy = input<StrategyEx>();

  private readonly sbCreatives = toSignal(
    toObservable(this.strategy).pipe(switchMap((strategy) => this.getSbCreatives(strategy))),
  );

  readonly activityFilter = computed<ActivityFilter[] | undefined>(() => {
    const strategy = this.strategy();
    const sbCreatives = this.sbCreatives();
    if (!strategy || !sbCreatives) {
      return undefined;
    }
    const audienceTargetings = strategy.isSdStrategy() ? strategy.audienceTargetings : [];
    return [
      { primaryType: EntityIdType.strategyId, primaryId: strategy.strategyId },
      ...sbCreatives.map((c) => ({ primaryType: EntityIdType.creativeId, primaryId: c.creativeId })),
      ...audienceTargetings.map((a) => ({ primaryType: EntityIdType.audienceTargetId, primaryId: a.audienceTargetId })),
    ];
  });

  private getSbCreatives(strategy: StrategyEx | undefined): Observable<SbCreative[]> {
    if (strategy?.campaignType !== CampaignType.SB) {
      return of([]);
    }
    return this.brandAssetsService
      .getSbCreativesPerStrategy(strategy.accountId, strategy.marketplace)
      .pipe(map((creatives) => creatives.get(strategy.strategyId!) ?? []));
  }
}
